import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  Card,
  Tabs,
  Button,
  Space,
  Tag,
  message,
  Spin
} from 'antd';
import {
  ArrowLeftOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  DashboardOutlined,
  CloudServerOutlined,
  LockOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  SettingOutlined,
  SafetyOutlined,
  ApiOutlined,
  BarChartOutlined,
  CodeOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  MedicineBoxOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { adminPath } from '../../../shared/config/paths';
import AdminSubscriptionEditModal from '../../components/billing/AdminSubscriptionEditModal';

const { TabPane } = Tabs;

export default function DomainDetail() {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [domainData, setDomainData] = useState(null);
  const [subEditOpen, setSubEditOpen] = useState(false);

  useEffect(() => {
    if (domainId) {
      loadDomainData();
    }
  }, [domainId]);

  const loadDomainData = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getDomain(domainId);
      if (response.success) {
        setDomainData(response.data);
      } else {
        message.error(response.error || '加载域名信息失败');
        navigate(adminPath('/cdn/domains'));
      }
    } catch (error) {
      console.error('Failed to load domain:', error);
      message.error('加载域名信息失败');
      navigate(adminPath('/cdn/domains'));
    } finally {
      setLoading(false);
    }
  };

  const reloadDomain = () => {
    loadDomainData();
  };

  // Get current menu key based on location
  const getCurrentMenuKey = () => {
    const path = location.pathname;
    const basePattern = adminPath(`/cdn/domains/${domainId}`);

    if (path === basePattern || path === `${basePattern}/`) {
      return 'overview';
    }

    if (path.includes('/origin')) return 'origin';
    if (path.includes('/ssl')) return 'ssl';
    if (path.includes('/firewall')) return 'firewall';
    if (path.includes('/rules')) return 'rules';
    if (path.includes('/cache')) return 'cache';
    if (path.includes('/optimization')) return 'optimization';
    if (path.includes('/network')) return 'network';
    if (path.includes('/traffic')) return 'traffic';
    if (path.includes('/worker')) return 'worker';
    if (path.includes('/error-pages')) return 'error-pages';
    if (path.includes('/diagnostics')) return 'diagnostics';
    if (path.includes('/plan')) return 'plan';
    if (path.includes('/settings')) return 'settings';

    return 'overview';
  };

  const handleTabChange = (key) => {
    const routes = {
      overview: '',
      origin: '/origin',
      ssl: '/ssl',
      firewall: '/firewall',
      rules: '/rules',
      cache: '/cache',
      optimization: '/optimization',
      network: '/network',
      traffic: '/traffic',
      worker: '/worker',
      'error-pages': '/error-pages',
      diagnostics: '/diagnostics',
      plan: '/plan',
      settings: '/settings'
    };

    const path = routes[key];
    navigate(adminPath(`/cdn/domains/${domainId}${path}`));
  };

  if (loading && !domainData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!domainData) {
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(adminPath('/cdn/domains'))}
        >
          返回列表
        </Button>
      </div>

      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {domainData.domain}
            </span>
          </Space>
        }
        extra={
          <Space>
            {!!domainData.ssl_enabled && (
              <Tag icon={<SafetyCertificateOutlined />} color="success">
                SSL已启用
              </Tag>
            )}
            {!!domainData.waf_enabled && (
              <Tag icon={<ThunderboltOutlined />} color="processing">
                WAF已启用
              </Tag>
            )}
            <Tag color="blue">用户ID: {domainData.user_id}</Tag>
            {domainData.subscription ? (
              <Button size="small" icon={<CalendarOutlined />} onClick={() => setSubEditOpen(true)}>
                {domainData.subscription.product_name || `#${domainData.subscription.product_id}`}
                {' · '}
                {domainData.subscription.expiry_date ? String(domainData.subscription.expiry_date).slice(0, 10) : '不过期'}
              </Button>
            ) : <Tag>无套餐</Tag>}
          </Space>
        }
      >
        <AdminSubscriptionEditModal
          open={subEditOpen}
          subscriptionId={domainData.subscription?.id}
          onClose={() => setSubEditOpen(false)}
          onSaved={loadDomainData}
        />
        <Tabs
          activeKey={getCurrentMenuKey()}
          onChange={handleTabChange}
          type="card"
        >
          <TabPane
            tab={
              <span>
                <DashboardOutlined />
                概览
              </span>
            }
            key="overview"
          />
          <TabPane
            tab={
              <span>
                <CloudServerOutlined />
                源站设置
              </span>
            }
            key="origin"
          />
          <TabPane
            tab={
              <span>
                <LockOutlined />
                SSL/TLS
              </span>
            }
            key="ssl"
          />
          <TabPane
            tab={
              <span>
                <SafetyOutlined />
                防火墙
              </span>
            }
            key="firewall"
          />
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                页面规则
              </span>
            }
            key="rules"
          />
          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                缓存
              </span>
            }
            key="cache"
          />
          <TabPane
            tab={
              <span>
                <ThunderboltOutlined />
                优化
              </span>
            }
            key="optimization"
          />
          <TabPane
            tab={
              <span>
                <ApiOutlined />
                网络
              </span>
            }
            key="network"
          />
          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                流量分析
              </span>
            }
            key="traffic"
          />
          <TabPane
            tab={
              <span>
                <CodeOutlined />
                Worker
              </span>
            }
            key="worker"
          />
          <TabPane
            tab={
              <span>
                <ExclamationCircleOutlined />
                错误页面
              </span>
            }
            key="error-pages"
          />
          <TabPane
            tab={
              <span>
                <MedicineBoxOutlined />
                诊断
              </span>
            }
            key="diagnostics"
          />
          <TabPane
            tab={
              <span>
                <ShoppingOutlined />
                套餐
              </span>
            }
            key="plan"
          />
          <TabPane
            tab={
              <span>
                <SettingOutlined />
                设置
              </span>
            }
            key="settings"
          />
        </Tabs>

        <div style={{ marginTop: 24 }}>
          <Outlet context={{ domainData, reloadDomain }} />
        </div>
      </Card>
    </div>
  );
}
